import _ from 'lodash'
import * as THREE from 'three'

import Config from '@glass-designer/config'
import MathsUtils from '@/utils/mathsUtils'

import GlassDesignerTypes from '../types'


/**
 * Functions associated with calculation of hulls for point sets
 * 
 * Uses the Monotone Chain Algorithm 
 * Based loosely on code from:
 * https://en.wikibooks.org/wiki/Algorithm_Implementation/Geometry/Convex_hull/Monotone_chain
 * 
 */
export default class EditorHullUtils {

  /**
   * Generates a list of points representing the convex hull from a list of GlassSegment objects
   * 
   * Returns an array of Vector2 objects
   */
  static generateSegmentListConvexHull(segmentList) {
    let shapeDivisions = Config.Settings.arcSubdivisions

    // Note: Ignore corner radiuses for the convex hull calculations
    // TODO: May need to avoid arcs for the purposes of straight line cutting

    let shape = MathsUtils.generateShapeFromSegmentList(segmentList, {
      isHole: false,
      ignoreCornerRadius: true
    })

    return EditorHullUtils.generatePointListConvexHull(shape.getPoints(shapeDivisions))
  }


  /**
   * Generates a list of points representing the convex hull of a list of 2D points
   * 
   * Returns an array of Vector2 objects
   */
  static generatePointListConvexHull(sourcePointList) {
    if(!_.isArray(sourcePointList) || sourcePointList.length == 0)
      return []

    var pointList = MathsUtils.removeConsecutive2DPointDuplicates(sourcePointList)

    // Sort by X
    pointList = pointList.sort((pointA, pointB) => {
      return (pointA.x - pointB.x) || (pointA.y - pointB.y)
    })

    let upperPoints = EditorHullUtils._calculateUpperTangent(pointList)
    let lowerPoints = EditorHullUtils._calculateLowerTangent(pointList)
    
    var convexPointList = lowerPoints.concat(upperPoints)
    convexPointList.push(pointList[0])

    // Optimise the hull point list
    convexPointList = MathsUtils.optimizePointList(convexPointList, {
      angleThreshold: 0.01,
      duplicateThreshold: 0.0001
    })

    return convexPointList
  }


  /**
   * Calculate the cross product value for 2 points compared to a reference point
   */
  static _calculateCross(reference, pointA, pointB) {
    return (pointA.x - reference.x) * (pointB.y - reference.y) - (pointA.y - reference.y) * (pointB.x - reference.x)
  }


  /**
   * Calculate the upper tanget points of a list of points for the convex hull
   * 
   * Returns an array of Vector2 objects
   */
  static _calculateUpperTangent(pointList) {
    var lowerPointList = []
    for (let l = 0; l < pointList.length; l++) {
      while (lowerPointList.length >= 2 && (EditorHullUtils._calculateCross(lowerPointList[lowerPointList.length - 2], lowerPointList[lowerPointList.length - 1], pointList[l]) <= 0)) {
        lowerPointList.pop()
      }
      lowerPointList.push(pointList[l])
    }
    lowerPointList.pop()
    return lowerPointList
  }


  /**
   * Calculate the lower tanget points of a list of points for the convex hull
   * 
   * Returns an array of Vector2 objects
   */
  static _calculateLowerTangent(pointList) {
    var reversedPoints = pointList.reverse()
    var upperPointList = []
    for (let u = 0; u < reversedPoints.length; u++) {
      while (upperPointList.length >= 2 && (EditorHullUtils._calculateCross(upperPointList[upperPointList.length - 2], upperPointList[upperPointList.length - 1], reversedPoints[u]) <= 0)) {
        upperPointList.pop()
      }
      upperPointList.push(reversedPoints[u])
    }
    upperPointList.pop()
    return upperPointList
  }
}